import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import Logo from "@/components/Logo";

const LINKS = [
  { id: "about", label: "Our Story" },
  { id: "divisions", label: "Divisions" },
  { id: "network", label: "Locations" },
  { id: "offers", label: "Mood Offers" },
  { id: "contact", label: "Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (window.__lenis) window.__lenis.scrollTo(`#${id}`, { offset: -70 });
    else document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-konattu-cream/80 backdrop-blur-2xl border-b border-konattu-charcoal/10" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-[1500px] mx-auto px-6 lg:px-10 h-[70px] flex items-center justify-between">
        <button onClick={() => go("top")} data-testid="nav-logo-btn" aria-label="Konattu Group home">
          <Logo />
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              data-testid={`nav-link-${l.id}`}
              className="font-body text-[13px] tracking-wide text-konattu-charcoal/70 hover:text-konattu-red transition-colors duration-300 relative group"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-konattu-red transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
          <button
            onClick={() => nav("/blog")}
            data-testid="nav-link-blog"
            className="font-body text-[13px] tracking-wide text-konattu-charcoal/70 hover:text-konattu-red transition-colors duration-300 relative group"
          >
            Journal
            <span className="absolute -bottom-1 left-0 h-px w-0 bg-konattu-red transition-all duration-300 group-hover:w-full" />
          </button>
          <button
            onClick={() => nav("/careers")}
            data-testid="nav-link-careers"
            className="font-body text-[13px] tracking-wide text-konattu-charcoal/70 hover:text-konattu-red transition-colors duration-300 relative group"
          >
            Careers
            <span className="absolute -bottom-1 left-0 h-px w-0 bg-konattu-red transition-all duration-300 group-hover:w-full" />
          </button>
        </nav>

        <button
          onClick={() => go("contact")}
          data-testid="nav-cta-btn"
          className="hidden md:inline-flex items-center bg-konattu-red text-white px-6 py-2.5 text-[13px] font-medium tracking-wide hover:bg-konattu-charcoal transition-colors duration-300"
        >
          Get in touch
        </button>

        <button className="md:hidden text-konattu-charcoal" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-konattu-cream border-b border-konattu-charcoal/10"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button key={l.id} onClick={() => go(l.id)} data-testid={`nav-mobile-${l.id}`} className="text-left font-display text-2xl text-konattu-charcoal hover:text-konattu-red transition-colors">
                  {l.label}
                </button>
              ))}
              <button onClick={() => nav("/careers")} data-testid="nav-mobile-careers" className="text-left font-display text-2xl text-konattu-charcoal hover:text-konattu-red transition-colors">
                Careers
              </button>
              <button onClick={() => nav("/blog")} data-testid="nav-mobile-blog" className="text-left font-display text-2xl text-konattu-charcoal hover:text-konattu-red transition-colors">
                Journal
              </button>
              <button onClick={() => go("contact")} className="bg-konattu-red text-white px-6 py-3 text-sm font-medium mt-2 w-full">
                Get in touch
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
